<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import MacroPreview from 'dashboard/routes/dashboard/conversation/Macros/MacroPreview.vue';

const props = defineProps({
  type: { type: String, required: true },
  id: { type: [Number, String], required: true },
  name: { type: String, default: '' },
  // Override the toggle row: custom label/styling instead of the default
  // "Preview" (e.g. the walkthrough summary uses "Macro: <name>" in blue),
  // and optionally without the name row above it.
  toggleLabel: { type: String, default: '' },
  toggleClass: { type: String, default: '' },
  showName: { type: Boolean, default: true },
});

const { t } = useI18n();
const expanded = ref(false);

const macros = useMapGetter('macros/getMacros');
// The cannedResponse Vuex module isn't namespaced, unlike macros.
const cannedResponses = useMapGetter('getCannedResponses');

const macro = computed(() =>
  macros.value.find(item => item.id === Number(props.id))
);
const cannedResponse = computed(() =>
  cannedResponses.value.find(item => item.id === Number(props.id))
);

const isMacroPreview = computed(() => props.type === 'macro' && macro.value);

// The popover-styled previews are reused inline: neutralize their floating
// chrome and hide their title heading (the accordion header already names
// it). Keep a sliver of left padding and visible overflow so the macro
// timeline's dots/line (absolutely positioned past the block's left edge)
// don't get clipped.
const INLINE_PREVIEW_CLASS =
  '!static !w-full !max-h-none !border-0 !bg-transparent !py-0 !pr-0 !pl-1 !overflow-visible !shadow-none !backdrop-blur-none [&>h6]:hidden';
</script>

<template>
  <div class="nodrag nopan flex min-w-0 flex-col mb-2">
    <slot v-if="showName" name="name">
      <span
        class="min-w-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
      >
        {{ name }}
      </span>
    </slot>
    <button
      type="button"
      class="mt-1 flex items-center gap-1 self-start !p-0 text-left"
      :class="toggleClass || 'text-xs text-n-slate-11 hover:text-n-slate-12'"
      @click.stop="expanded = !expanded"
    >
      <span class="min-w-0 whitespace-pre-wrap break-words">
        {{ toggleLabel || t('SOPS.PICKER.PREVIEW') }}
      </span>
      <span
        class="i-lucide-chevron-down size-3.5 shrink-0 transition-transform"
        :class="{ 'rotate-180': expanded }"
      />
    </button>
    <div v-if="expanded" class="mt-1.5 border-t border-n-weak pt-1.5">
      <MacroPreview
        v-if="isMacroPreview"
        :macro="macro"
        :class="INLINE_PREVIEW_CLASS"
      />
      <p
        v-else
        class="bg-n-solid-3 p-2 rounded whitespace-pre-wrap break-words text-xs text-n-slate-11"
      >
        {{ cannedResponse?.content || t('SOPS.PICKER.NO_SUMMARY') }}
      </p>
    </div>
  </div>
</template>
